<template>
  <div
    v-if="shouldRender"
    :class="wrapperClasses"
    @click="handleOverlayClick"
  >
    <div
      :class="cardClasses"
      @click.stop
      v-motion
      :initial="{ opacity: 0, scale: 0.8 }"
      :enter="{ opacity: 1, scale: 1 }"
      :leave="{ opacity: 0, scale: 0.8 }"
      :duration="300"
    >
      <!-- Header -->
      <div class="flex justify-between items-center mb-6 border-b border-gray-200 pb-4">
        <component
          :is="isPage ? 'h1' : 'h2'"
          class="text-3xl font-poppins font-bold text-dark-gray"
        >Cookie Policy</component>
        <button
          v-if="!isPage"
          @click="closeModal"
          class="w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors duration-200"
        >
          <X :size="20" class="text-dark-gray" />
        </button>
      </div>

      <!-- Content -->
      <div class="prose prose-gray max-w-none">
        <p class="text-sm text-gray-500 mb-6">Last updated: {{ lastUpdated }}</p>
        
        <section class="mb-8">
          <h3 class="text-xl font-semibold text-dark-gray mb-4">1. What Are Cookies</h3>
          <p class="text-dark-gray/80">
            Cookies are small text files that are placed on your computer or mobile device when you 
            visit our website. They are widely used to make websites work more efficiently and to 
            provide information to website owners.
          </p>
        </section>

        <section class="mb-8">
          <h3 class="text-xl font-semibold text-dark-gray mb-4">2. How We Use Cookies</h3>
          <p class="text-dark-gray/80 mb-4">
            Homvisit uses cookies for the following purposes:
          </p>
          <ul class="list-disc pl-6 text-dark-gray/80 space-y-2">
            <li><strong>Essential Cookies:</strong> Required for the website to function properly</li>
            <li><strong>Performance Cookies:</strong> Help us understand how visitors interact with our website</li>
            <li><strong>Functionality Cookies:</strong> Remember your preferences and settings</li>
            <li><strong>Analytics Cookies:</strong> Help us improve our website and services</li>
          </ul>
        </section>

        <section class="mb-8">
          <h3 class="text-xl font-semibold text-dark-gray mb-4">3. Types of Cookies We Use</h3>
          
          <div class="space-y-6">
            <div class="p-4 bg-light-purple/5 rounded-lg">
              <h4 class="font-semibold text-dark-gray mb-2">Session Cookies</h4>
              <p class="text-dark-gray/80 text-sm">
                These cookies are temporary and are deleted when you close your browser. 
                They help us maintain your session while you navigate our website.
              </p>
            </div>

            <div class="p-4 bg-light-purple/5 rounded-lg">
              <h4 class="font-semibold text-dark-gray mb-2">Persistent Cookies</h4>
              <p class="text-dark-gray/80 text-sm">
                These cookies remain on your device for a set period or until you delete them. 
                They help us remember your preferences for future visits.
              </p>
            </div>

            <div class="p-4 bg-light-purple/5 rounded-lg">
              <h4 class="font-semibold text-dark-gray mb-2">Third-Party Cookies</h4>
              <p class="text-dark-gray/80 text-sm">
                We may use third-party services like Google Analytics that place their own cookies 
                to help us understand website usage and improve our services.
              </p>
            </div>
          </div>
        </section>

        <section class="mb-8">
          <h3 class="text-xl font-semibold text-dark-gray mb-4">4. Managing Cookies</h3>
          <p class="text-dark-gray/80 mb-4">
            You can control and manage cookies in several ways:
          </p>
          <ul class="list-disc pl-6 text-dark-gray/80 space-y-2">
            <li>Browser Settings: Most browsers allow you to refuse or delete cookies</li>
            <li>Opt-out Tools: Use third-party opt-out tools for analytics cookies</li>
            <li>Website Settings: Some websites provide cookie preference centers</li>
          </ul>
          <p class="text-dark-gray/80 mt-4 text-sm italic">
            Note: Disabling certain cookies may affect the functionality of our website.
          </p>
        </section>

        <section class="mb-8">
          <h3 class="text-xl font-semibold text-dark-gray mb-4">5. Cookie Retention</h3>
          <p class="text-dark-gray/80">
            Different cookies have different retention periods:
          </p>
          <ul class="list-disc pl-6 text-dark-gray/80 space-y-2 mt-4">
            <li>Session cookies: Deleted when you close your browser</li>
            <li>Preference cookies: Typically retained for 1 year</li>
            <li>Analytics cookies: Usually retained for 2 years</li>
          </ul>
        </section>

        <section class="mb-8">
          <h3 class="text-xl font-semibold text-dark-gray mb-4">6. Updates to This Policy</h3>
          <p class="text-dark-gray/80">
            We may update this Cookie Policy from time to time. Any changes will be posted on this page 
            with an updated revision date. We encourage you to review this policy periodically.
          </p>
        </section>

        <section class="mb-8">
          <h3 class="text-xl font-semibold text-dark-gray mb-4">7. Contact Us</h3>
          <p class="text-dark-gray/80">
            If you have questions about our use of cookies, please contact us:
          </p>
          <div class="mt-4 p-4 bg-light-purple/10 rounded-lg">
            <p class="text-dark-gray font-medium">Email: sujithvijayan@homvisit.com</p>
            <p class="text-dark-gray font-medium">Phone: 75 9409 9409</p>
            <p class="text-dark-gray font-medium">Address: Homvisit, Trivandrum, Kerala</p>
          </div>
        </section>
      </div>
    </div>
  </div>
</template>

<script>
import { computed } from 'vue'
import { X } from 'lucide-vue-next'
import {
  breadcrumbSchema,
  graphSchema,
  organizationSchema,
  useSeo,
} from '../composables/useSeo'

export default {
  name: 'CookiePolicy',
  components: {
    X
  },
  props: {
    isVisible: {
      type: Boolean,
      default: false
    },
    isPage: {
      type: Boolean,
      default: false
    }
  },
  emits: ['close'],
  setup(props, { emit }) {
    const lastUpdated = computed(() => new Date().toLocaleDateString())
    const shouldRender = computed(() => props.isPage || props.isVisible)
    const wrapperClasses = computed(() =>
      props.isPage
        ? 'min-h-screen bg-gradient-to-b from-white to-light-purple/10 py-16 px-4 flex justify-center items-start'
        : 'fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4'
    )
    const cardClasses = computed(() =>
      props.isPage
        ? 'card-neumorphic max-w-4xl w-full shadow-xl'
        : 'card-neumorphic max-w-4xl w-full max-h-[90vh] overflow-y-auto'
    )

    if (props.isPage) {
      useSeo({
        title: 'Cookie Policy | Homvisit Website Cookies Guide Kerala',
        description:
          'Learn which cookies Homvisit uses on homvisit.com, why they are used, and how you can control cookie preferences while browsing our Kerala service pages.',
        path: '/cookie-policy',
        schema: graphSchema([
          organizationSchema(),
          breadcrumbSchema([
            { name: 'Home', path: '/' },
            { name: 'Cookie Policy', path: '/cookie-policy' },
          ]),
        ]),
      })
    }

    const closeModal = () => {
      emit('close')
    }

    const handleOverlayClick = () => {
      if (!props.isPage) {
        closeModal()
      }
    }

    return {
      lastUpdated,
      shouldRender,
      wrapperClasses,
      cardClasses,
      closeModal,
      handleOverlayClick
    }
  }
}
</script>
